import { useState } from "react";
import { Upload, Download, FileUp } from "lucide-react";
import { useCsvImport } from "../hooks/useCsvImport";
import { parseCsvRows, downloadCsvTemplate } from "../lib/csvImport";
import { AssetImportPreview } from "./ImportPreview";
import { FormDialog, Field, ActionButton } from "./common";

const HEADER_MAP = {
  fixed_asset_id: "fixed_asset_id", fixed_asset: "fixed_asset_id", fa_id: "fixed_asset_id", id: "fixed_asset_id", asset_id: "fixed_asset_id",
  asset_name: "asset_name", name: "asset_name", asset: "asset_name",
  alternate_asset_name: "alt_asset_name", alt_asset_name: "alt_asset_name", alternate_name: "alt_asset_name",
  asset_description: "description", description: "description",
  asset_type: "asset_type", type: "asset_type",
  operation_status: "operation_status", operational_status: "operation_status", op_status: "operation_status",
  asset_memo: "memo", memo: "memo",
  class: "asset_class", asset_class: "asset_class",
  location: "location",
  department: "department", dept: "department",
  driver_name: "driver_name", driver: "driver_name",
  year_of_manufacturing: "year_of_manufacturing", year: "year_of_manufacturing", year_of_manufacture: "year_of_manufacturing",
  make: "make", model: "model",
  vin_serial: "vin_serial", vin: "vin_serial", serial: "vin_serial", serial_number: "vin_serial", "serial_#": "vin_serial", "asset_vin_or_serial_#": "vin_serial",
  license_plate: "license_plate", license_plate_no: "license_plate", plate: "license_plate",
  registration_renewal: "registration_renewal",
  coupled_unit: "coupled_unit",
  gps_gateway: "gps_gateway",
  asset_is_leased: "is_leased", is_leased: "is_leased", leased: "is_leased",
  monthly_lease_value: "monthly_lease_value",
  lease_start_date: "lease_start_date",
  lease_expiration_date: "lease_expiration_date",
  lienholder: "lienholder",
  financed_note_value: "financed_note_value",
  liability_account: "liability_account",
  asset_account: "asset_account", ledger_account: "asset_account",
  asset_status: "asset_status",
  depreciation_active: "depreciation_active",
  last_depreciation_date: "last_depreciation_date",
  current_net_book_value: "current_net_book_value", net_book_value: "current_net_book_value", nbv: "current_net_book_value",
  depreciation_end_date: "depreciation_end_date",
  depreciation_method: "depreciation_method",
  depreciation_period: "depreciation_period",
  asset_lifetime: "asset_lifetime", lifetime: "asset_lifetime",
  purchase_date: "purchase_date",
  asset_current_cost: "current_cost", current_cost: "current_cost",
  asset_original_cost: "original_cost", original_cost: "original_cost", purchase_cost: "original_cost",
  disposal_date: "disposal_date",
  subsidiary: "subsidiary",
  internal_id: "internal_id",
};

const TEMPLATE = "fixed_asset_id,asset_name,alt_asset_name,description,asset_type,operation_status,memo,asset_class,location,department,driver_name,year_of_manufacturing,make,model,vin_serial,license_plate,registration_renewal,coupled_unit,gps_gateway,is_leased,monthly_lease_value,lease_start_date,lease_expiration_date,lienholder,financed_note_value,liability_account,asset_account,asset_status,depreciation_active,last_depreciation_date,current_net_book_value,depreciation_end_date,depreciation_method,depreciation_period,asset_lifetime,purchase_date,current_cost,original_cost,disposal_date,subsidiary,internal_id\nFA-1008,TRK-105,Unit 105,2023 Kenworth T880 vac truck,truck,active,DOT due Oct,Heavy Truck,Yard 4,SWD Operations,Jesse Holt,2023,Kenworth,T880,1XKZD40X1PJ555001,TX-99A100,2027-02-28,,Samsara GW-105,yes,3100,2026-01-01,2029-12-31,DLL Finance,150000,2510-NOTES,1510-TRUCKS,in_service,yes,,170000,,straight_line,monthly,7,2024-01-10,182000,182000,,PBS Trucking LLC,A-0008\n";

const NUMERIC = ["year_of_manufacturing", "monthly_lease_value", "financed_note_value", "current_net_book_value", "asset_lifetime", "current_cost", "original_cost"];
const BOOLS = ["is_leased", "depreciation_active"];

const parseAssets = (raw) => parseCsvRows(raw, HEADER_MAP, "asset_name", NUMERIC, BOOLS);

export const AssetImport = ({ open, onOpenChange, onDone }) => {
  const [updateExisting, setUpdateExisting] = useState(false);
  const { rows, parseErrors, csvText, busy, fileRef, reset, onFile, onPaste, submit } = useCsvImport({
    parse: parseAssets, endpoint: "/assets/import", entity: "assets", onOpenChange, onDone,
    extraBody: () => ({ update_existing: updateExisting }),
  });
  const downloadTemplate = () => downloadCsvTemplate(TEMPLATE, "assets_import_template.csv");

  return (
    <FormDialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }} title="Import Assets" testId="asset-import-dialog">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400 leading-relaxed max-w-[300px]">
          CSV columns: <span className="font-mono text-amber-400/90">asset_name</span> required — <span className="font-mono text-amber-400/90">fixed_asset_id</span> and all register fields supported (see template)
        </p>
        <button type="button" data-testid="asset-import-template-download" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-sky-400 hover:text-sky-300 transition-colors duration-200">
          <Download size={13} /> Template
        </button>
      </div>
      <Field label="Upload CSV file">
        <label
          data-testid="asset-import-file-drop"
          className="flex items-center justify-center gap-2 border border-dashed border-slate-600 rounded-md px-4 py-5 text-sm text-slate-400 hover:border-amber-500/50 hover:text-amber-300 cursor-pointer transition-colors duration-200"
        >
          <FileUp size={16} /> Choose .csv file
          <input ref={fileRef} data-testid="asset-import-file-input" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
      </Field>

      <Field label="…or paste CSV text">
        <textarea
          data-testid="asset-import-csv-textarea"
          value={csvText}
          onChange={(e) => onPaste(e.target.value)}
          rows={5}
          placeholder={"fixed_asset_id,asset_name,description,asset_type,operation_status,asset_class,department,...\nFA-1008,TRK-105,2023 Kenworth T880 vac truck,truck,active,Heavy Truck,SWD Operations"}
          className="w-full bg-slate-900/80 border border-slate-700 rounded-md px-3 py-2 text-xs font-mono text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors duration-200"
        />
      </Field>

      {parseErrors.length > 0 && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3 space-y-1" data-testid="asset-import-parse-errors">
          {parseErrors.slice(0, 5).map((e) => (
            <p key={e} className="text-xs text-red-400 font-mono">{e}</p>
          ))}
        </div>
      )}

      <AssetImportPreview rows={rows} />

      <label data-testid="asset-import-update-existing-label" className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid="asset-import-update-existing"
          checked={updateExisting}
          onChange={(e) => setUpdateExisting(e.target.checked)}
          className="mt-0.5 accent-amber-500"
        />
        <span>
          <span className="font-medium text-slate-200">Update existing assets</span> — if an Asset Name already exists, fill in / overwrite its fields (e.g. a missing ID) instead of skipping it as a duplicate.
        </span>
      </label>

      <ActionButton testId="asset-import-submit-button" onClick={submit} disabled={busy || rows.length === 0} className="w-full justify-center">
        <Upload size={16} /> {busy ? (updateExisting ? "Importing / updating…" : "Importing…") : `${updateExisting ? "Import / Update" : "Import"} ${rows.length > 0 ? rows.length : ""} Asset${rows.length === 1 ? "" : "s"}`}
      </ActionButton>
    </FormDialog>
  );
};
